import { format } from 'date-fns'
import { checkinEntryLabel } from '../constants/checkinEntryLabels.constant'
import { directiveEntry } from '../constants/directiveEntryLabels.constant'
import { AuctionCardData, AuctionProposal } from '../types/auctionCard.type'
import {
  AuctionAvailableData,
  AuctionFollowingData,
  AuctionProposalContents,
  AuctionSummaryProposal,
  AuctionUpdatedSummaryData,
} from '../types/auctionSummary.type'

export class AuctionSummaryTransformer {
  private static formatDay(iso?: string | null) {
    return iso ? format(new Date(iso), 'dd/MM') : ''
  }

  private static mapContents(contents?: AuctionProposalContents | null): AuctionProposal | undefined {
    if (!contents) return undefined

    return {
      total: contents.total_value != null ? Number(contents.total_value) : undefined,
      discountPercent: contents.difference_percentage != null ? Number(contents.difference_percentage) : undefined,
      estimatedCompletionDate: contents.estimated_completion_date
        ? AuctionSummaryTransformer.formatDay(contents.estimated_completion_date)
        : undefined,
      deadlineDays: contents.deadline_to_complete_in_days ?? 0,
      startDate: AuctionSummaryTransformer.formatDay(contents.start_at),
    }
  }

  private static mapProposal(proposal?: AuctionSummaryProposal | null) {
    return proposal ? AuctionSummaryTransformer.mapContents(proposal.contents) : undefined
  }

  private static contentsEqual(a?: AuctionProposalContents | null, b?: AuctionProposalContents | null) {
    return (
      !!a &&
      !!b &&
      a.start_at === b.start_at &&
      a.deadline_to_complete_in_days === b.deadline_to_complete_in_days &&
      a.total_value === b.total_value &&
      a.estimated_completion_date === b.estimated_completion_date
    )
  }

  static mapAvailableAuctionToCard() {
    return (items: AuctionAvailableData[]): AuctionCardData[] =>
      (items ?? []).map((item) => {
        const { forwarding } = item
        const { label, metric } = directiveEntry(forwarding?.directive_entry_type)

        return {
          id: item.id,
          status: 'awaiting',
          forwardingType: label,
          metricKind: metric,
          companyName: forwarding?.equipment?.company?.general_information?.fantasy_name ?? '',
          equipmentModel: forwarding?.equipment?.model?.display_name ?? '',
          transportType: checkinEntryLabel(forwarding?.checkin_entry_type),
          startsAt: item.start_at ?? undefined,
          endsAt: item.finish_at,
          participants: item.participants_count,
          bestProposal: AuctionSummaryTransformer.mapContents(item.best_proposal?.proposal_content),
          original:
            metric === 'date' || forwarding?.directive_entry?.total_value == null
              ? undefined
              : { total: Number(forwarding.directive_entry.total_value) },
          isSealed: item.kind === 'closed',
        }
      })
  }

  static mapFollowingAuctionToCard(status: AuctionCardData['status']) {
    return (items: AuctionFollowingData[]): AuctionCardData[] =>
      (items ?? []).map((item) => {
        const { forwarding } = item
        const { label, metric } = directiveEntry(forwarding?.directive_entry_type)
        const yourContent = item.your_proposal?.proposal_content
        const bestContent = item.best_proposal?.proposal_content
        const leading = !!yourContent && AuctionSummaryTransformer.contentsEqual(yourContent, bestContent)

        return {
          id: item.id,
          status,
          forwardingType: label,
          metricKind: metric,
          companyName: forwarding?.equipment?.company?.general_information?.fantasy_name ?? '',
          equipmentModel: forwarding?.equipment?.model?.display_name ?? '',
          transportType: checkinEntryLabel(forwarding?.checkin_entry_type),
          startsAt: item.start_at ?? undefined,
          endsAt: item.finish_at,
          participants: item.participants_count,
          myPosition: leading ? 1 : undefined,
          bestProposal: AuctionSummaryTransformer.mapContents(bestContent),
          secondProposal: AuctionSummaryTransformer.mapContents(item.second_proposal?.proposal_content),
          myProposal: AuctionSummaryTransformer.mapContents(yourContent),
          original:
            metric === 'date' || forwarding?.directive_entry?.total_value == null
              ? undefined
              : { total: Number(forwarding.directive_entry.total_value) },
          cancelled: status === 'closed' && item.status === 'canceled',
          paused: item.paused_at != null,
          hasSecondProposal: item.second_proposal != null,
          isSealed: item.kind === 'closed',
        }
      })
  }

  static mapAuctionSummaryToCard(status: AuctionCardData['status']) {
    return (summary: AuctionUpdatedSummaryData): AuctionCardData => {
      const { label, metric } = directiveEntry(summary.directive_entry_type)

      return {
        id: summary.id,
        status,
        forwardingType: label,
        metricKind: metric,
        companyName: '',
        equipmentModel: summary.equipment_model_name ?? '',
        transportType: checkinEntryLabel(summary.checkin_entry_type),
        isSealed: summary.kind ? summary.kind !== 'open' : undefined,
        startsAt: summary.start_at ?? undefined,
        endsAt: summary.finish_at,
        participants: summary.participants_count,
        myPosition: summary.your_proposal?.position,
        bestProposal: AuctionSummaryTransformer.mapProposal(summary.best_proposal),
        secondProposal: AuctionSummaryTransformer.mapProposal(summary.second_proposal),
        myProposal: AuctionSummaryTransformer.mapProposal(summary.your_proposal),
        original: AuctionSummaryTransformer.mapProposal(summary.original),
        cancelled: summary.status === 'canceled',
        paused: summary.paused_at != null,
        hasSecondProposal: summary.second_proposal != null,
      }
    }
  }
}
